import React from "react";
import { Helmet } from "react-helmet-async";
import { buildCanonicalUrl, buildMetaDescription, toAbsoluteUrl } from "./seoUtils";

const DEFAULTS = {
  siteName: "Cherrfy",
  title: "Cherrfy Ecommerce",
  description:
    "Cherrfy is a trusted cross-border ecommerce platform offering easy ordering, secure checkout, and reliable delivery.",
  imagePath: "/og-default.svg",
  locale: "en_US",
};

const joinTitle = (pageTitle) => {
  const t = String(pageTitle || "").trim();
  if (!t) return DEFAULTS.title;
  if (t.toLowerCase().includes(DEFAULTS.siteName.toLowerCase())) return t;
  return `${t} | ${DEFAULTS.siteName}`;
};

export default function Seo({
  title,
  description,
  canonical,
  pathname,
  search,
  image,
  type = "website",
  noIndex = false,
  robots,
  ogTitle,
  ogDescription,
  ogImage,
  ogType,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  jsonLd,
  useDefaults = true,
}) {
  const finalTitle = useDefaults ? joinTitle(title) : String(title || "").trim();
  const finalDescription = useDefaults
    ? buildMetaDescription(description, DEFAULTS.description)
    : (description ? buildMetaDescription(description, "") : "");
  const finalCanonical = canonical || (useDefaults ? buildCanonicalUrl(pathname, search) : "");
  const finalOgTitle = ogTitle ? (useDefaults ? joinTitle(ogTitle) : String(ogTitle).trim()) : finalTitle;
  const finalOgDescription = ogDescription
    ? buildMetaDescription(ogDescription, finalDescription)
    : finalDescription;
  const finalOgType = ogType || type;

  const finalTwitterTitle = twitterTitle
    ? (useDefaults ? joinTitle(twitterTitle) : String(twitterTitle).trim())
    : finalTitle;
  const finalTwitterDescription = twitterDescription
    ? buildMetaDescription(twitterDescription, finalDescription)
    : finalDescription;
  const finalTwitterCard = twitterCard || (useDefaults ? "summary_large_image" : "");
  const finalRobots = noIndex ? "noindex,nofollow" : robots;

  const fallbackImage = useDefaults ? DEFAULTS.imagePath : "";
  const absoluteImage = toAbsoluteUrl(ogImage || image || fallbackImage);
  const absoluteTwitterImage = toAbsoluteUrl(twitterImage || ogImage || image || fallbackImage);

  return (
    <Helmet>
      {finalTitle ? <title>{finalTitle}</title> : null}
      {finalDescription ? <meta name="description" content={finalDescription} /> : null}

      {finalCanonical ? <link rel="canonical" href={finalCanonical} /> : null}

      {finalRobots ? <meta name="robots" content={finalRobots} /> : null}

      {/* Open Graph */}
      {useDefaults ? <meta property="og:site_name" content={DEFAULTS.siteName} /> : null}
      {finalOgType ? <meta property="og:type" content={finalOgType} /> : null}
      {finalOgTitle ? <meta property="og:title" content={finalOgTitle} /> : null}
      {finalOgDescription ? <meta property="og:description" content={finalOgDescription} /> : null}
      {finalCanonical ? <meta property="og:url" content={finalCanonical} /> : null}
      {absoluteImage ? <meta property="og:image" content={absoluteImage} /> : null}
      {useDefaults ? <meta property="og:locale" content={DEFAULTS.locale} /> : null}

      {/* Twitter */}
      {finalTwitterCard ? <meta name="twitter:card" content={finalTwitterCard} /> : null}
      {finalTwitterTitle ? <meta name="twitter:title" content={finalTwitterTitle} /> : null}
      {finalTwitterDescription ? <meta name="twitter:description" content={finalTwitterDescription} /> : null}
      {absoluteTwitterImage ? <meta name="twitter:image" content={absoluteTwitterImage} /> : null}

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
